import { getDownloadURL, ref, uploadBytes } from "firebase/storage";
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import panZoomIcon from "../assets/disaster-icons/pan_zoom.svg";
import deleteFIleIcon from "../assets/imageUploader/delete-file-icon.png";
import fileUploadIcon from "../assets/imageUploader/file-upload-icon.png";
import { storage } from "../firebase";
import { formatDateMMDDYYYY, imageNameFormatToDate } from "../utils/formatter";
import DeleteModal from "./DeleteModal";
import Modal from "./Modal";
import ModalFileViewer from "./ModalFileViewer";
import PdfViewer from "./PDFViewer";
import ViewPhotoOverlay from "./ViewPhotoOverlay";

const ViewPhotosModal = ({
  isOpen,
  multiple = false,
  max = 5,
  onClose,
  photosData = [],
  storagePath,
  dateKey = "dateAdded",
  handleCollectionUpdate,
  onEditClick,
  isLoadingDelete = false,
  handleDelete,
  acceptedTypes = "image/*",
  customOnRightButtonClick,
  customOnLeftButtonClick,
  hasVerifiedDisplay = false,
  hasDescriptionDisplay = false,
  descriptionKey = "description",
  onGetCurrentIndex,
  rightContent,
  leftContent,
  disabled = false,
  title,
}) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isFullViewOpen, setIsFullViewOpen] = useState(false);
  const [photos, setPhotos] = useState(photosData || []);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const fileInputRef = useRef(null);

  //   const [currentPhoto, setCurrentPhoto] = useState(photosData[0]);

  useEffect(() => {
    if (isOpen && photosData?.length > 0) {
      setPhotos(photosData || []);
    }
  }, [photosData, isOpen]);

  const handleNavigation = useCallback(
    (direction) => {
      setCurrentIndex((prev) => {
        if (direction === "next") {
          const index = (prev + 1) % photos.length;
          onGetCurrentIndex?.(index);
          return index;
        } else {
          const index = (prev - 1 + photos.length) % photos.length;
          onGetCurrentIndex?.(index);
          return index;
        }
      });
    },
    [photos]
  );

  const renderPhoto = useMemo(() => {
    if (photos?.length === 0) {
      return null;
    } else if (photos?.length === 1) {
      return photos[0];
    } else {
      return photos[currentIndex];
    }
  }, [currentIndex, photos]);

  const renderFilePreview = useCallback(
    (file) => {
      if (!file) return null;
      const isPDF =
        file.src?.toLowerCase().includes(".pdf") || file.type?.includes("pdf");

      if (isPDF) {
        return (
          <div className="h-full w-full overflow-hidden">
            <PdfViewer fileUrl={file?.src} />
          </div>
          // <div className="bg-gray-100 flex h-full w-full flex-col items-center justify-center">
          //   <svg
          //     className="h-20 w-20 text-gray-400"
          //     fill="none"
          //     viewBox="0 0 24 24"
          //     stroke="currentColor"
          //   >
          //     <path
          //       strokeLinecap="round"
          //       strokeLinejoin="round"
          //       strokeWidth={2}
          //       d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z"
          //     />
          //   </svg>

          //   <p className="text-gray-600 mt-2 text-sm">
          //     {file?.name || "PDF Document"}
          //   </p>
          // </div>
        );
      } else {
        return (
          <img
            draggable={false}
            src={file?.src || file?.[0]?.src}
            className="h-full w-full object-cover"
            alt={file?.name || `uploaded-${currentIndex}`}
          />
        );
      }
    },
    [currentIndex]
  );

  const renderFiles = useMemo(() => {
    if (photos?.length === 0) {
      return null;
    }

    return renderFilePreview(renderPhoto);
  }, [photos, renderFilePreview, renderPhoto]);

  const handleFileUpload = useCallback(
    async (e) => {
      if (!e.target.files || !storagePath) {
        alert("Something went wrong while uploading the file.");
        return;
      }
      try {
        if (multiple) {
          setIsUploading(true);
          const files = Array.from(e.target.files);
          const uploadedFiles = await Promise.all(
            files.slice(0, max).map(async (fileObj) => {
              const { name, type } = fileObj || {};
              const formattedFileName = imageNameFormatToDate(name);
              const formattedPath = `${storagePath}/${formattedFileName}`;
              const formattedUploadRef = ref(storage, formattedPath);

              const uploadFile = await uploadBytes(formattedUploadRef, fileObj);
              const downloadUrl = await getDownloadURL(uploadFile.ref);
              const newFile = {
                src: downloadUrl,
                name,
                type,
                [dateKey]: new Date().toISOString(),
                details: "",
              };
              return newFile;
              // fileInputRef.current.value = "";
            })
          );
          if (handleCollectionUpdate) {
            await handleCollectionUpdate(uploadedFiles || []);
            const newPhotos = [...photos, ...(uploadedFiles || [])];
            setPhotos(newPhotos);
            setCurrentIndex(newPhotos.length - 1);
          }
          setIsUploading(false);
        } else {
          setIsUploading(true);
          const originalFile = e.target.files[0];
          const formattedFileName = imageNameFormatToDate(originalFile.name);
          const formattedPath = `${storagePath}/${formattedFileName}`;
          const formattedUploadRef = ref(storage, formattedPath);

          const uploadFile = await uploadBytes(
            formattedUploadRef,
            originalFile
          );
          const downloadUrl = await getDownloadURL(uploadFile.ref);
          const newFile = {
            src: downloadUrl,
            name: e.target.files[0]?.name,
            type: e.target.files[0]?.type,
            [dateKey]: new Date().toISOString(),
            details: "",
          };
          if (handleCollectionUpdate) {
            await handleCollectionUpdate(newFile);
            const newPhotos = [...photos, newFile];
            setPhotos(newPhotos);
            setCurrentIndex(newPhotos.length - 1);
          }
          // fileInputRef.current.value = "";
          setIsUploading(false);
        }
      } catch (error) {
        console.error("Error uploading file:", error);
      }
    },
    [storagePath, photos, dateKey, handleCollectionUpdate, max, multiple]
  );

  return (
    <>
      <DeleteModal
        isOpen={isDeleteModalOpen}
        onClose={() => {
          setIsDeleteModalOpen(false);
        }}
        loading={isLoadingDelete}
        onDelete={async () => {
          const newData = photos.filter((_, index) => index !== currentIndex);
          await handleDelete(newData);
          setPhotos(newData);
          setIsDeleteModalOpen(false);
          setCurrentIndex(Math.max(currentIndex - 1, 0));
        }}
      />
      <Modal
        isOpen={isOpen}
        onClose={() => {
          onClose();
          setCurrentIndex(0);
          setIsFullViewOpen(false);
          setPhotos([]);
        }}
        title={title || renderPhoto?.name || "View Photo"}
        customOnLeftButtonClick={() => {
          if (customOnLeftButtonClick) {
            customOnLeftButtonClick();
          } else {
            if (onEditClick) {
              onEditClick(photos);
            }
          }
        }}
        customLeftButtonText="Edit File"
        customOnRightButtonClick={() => {
          if (customOnRightButtonClick) {
            customOnRightButtonClick();
          } else {
            fileInputRef?.current?.click();
          }
        }}
        customRightButtonText="Upload New File"
        customRightButton={
          <input
            ref={fileInputRef}
            type="file"
            className="hidden"
            accept={acceptedTypes}
            multiple={multiple}
            onChange={handleFileUpload}
          />
        }
        hideLeftButton={!photos?.length}
        isLoading={isUploading}
        leftContent={leftContent}
        rightContent={rightContent}
        noFooter={disabled}
      >
        {photos?.length > 0 ? (
          <div className=" space-y-4">
            {photos?.length > 1 && (
              <div className="flex justify-center space-x-2">
                {photos?.map((_, index) => (
                  <div
                    key={index}
                    className={`h-3 w-3 rounded-full transition-all duration-300 ${
                      index === currentIndex ? "bg-blue-900" : "bg-gray-400"
                    } cursor-pointer`}
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      setCurrentIndex(index);
                      onGetCurrentIndex?.(index);
                      // setCurrentPhoto(photosData[index]);
                    }}
                  ></div>
                ))}
              </div>
            )}
            <div className="relative flex h-[300px] rounded-md border border-solid border-[#CFCFCF]">
              <ViewPhotoOverlay />
              <div className="absolute bottom-6 left-5 flex items-center gap-x-2">
                <svg
                  width="18"
                  height="20"
                  viewBox="0 0 18 20"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M2 19.9922C1.45 19.9922 0.979167 19.7964 0.5875 19.4047C0.195833 19.013 0 18.5422 0 17.9922V3.99219C0 3.44219 0.195833 2.97135 0.5875 2.57969C0.979167 2.18802 1.45 1.99219 2 1.99219H3V0.992188C3 0.708854 3.09583 0.471354 3.2875 0.279687C3.47917 0.0880208 3.71667 -0.0078125 4 -0.0078125C4.28333 -0.0078125 4.52083 0.0880208 4.7125 0.279687C4.90417 0.471354 5 0.708854 5 0.992188V1.99219H13V0.992188C13 0.708854 13.0958 0.471354 13.2875 0.279687C13.4792 0.0880208 13.7167 -0.0078125 14 -0.0078125C14.2833 -0.0078125 14.5208 0.0880208 14.7125 0.279687C14.9042 0.471354 15 0.708854 15 0.992188V1.99219H16C16.55 1.99219 17.0208 2.18802 17.4125 2.57969C17.8042 2.97135 18 3.44219 18 3.99219V17.9922C18 18.5422 17.8042 19.013 17.4125 19.4047C17.0208 19.7964 16.55 19.9922 16 19.9922H2ZM2 17.9922H16V7.99219H2V17.9922ZM9 11.9922C8.71667 11.9922 8.47917 11.8964 8.2875 11.7047C8.09583 11.513 8 11.2755 8 10.9922C8 10.7089 8.09583 10.4714 8.2875 10.2797C8.47917 10.088 8.71667 9.99219 9 9.99219C9.28333 9.99219 9.52083 10.088 9.7125 10.2797C9.90417 10.4714 10 10.7089 10 10.9922C10 11.2755 9.90417 11.513 9.7125 11.7047C9.52083 11.8964 9.28333 11.9922 9 11.9922ZM5 11.9922C4.71667 11.9922 4.47917 11.8964 4.2875 11.7047C4.09583 11.513 4 11.2755 4 10.9922C4 10.7089 4.09583 10.4714 4.2875 10.2797C4.47917 10.088 4.71667 9.99219 5 9.99219C5.28333 9.99219 5.52083 10.088 5.7125 10.2797C5.90417 10.4714 6 10.7089 6 10.9922C6 11.2755 5.90417 11.513 5.7125 11.7047C5.52083 11.8964 5.28333 11.9922 5 11.9922ZM13 11.9922C12.7167 11.9922 12.4792 11.8964 12.2875 11.7047C12.0958 11.513 12 11.2755 12 10.9922C12 10.7089 12.0958 10.4714 12.2875 10.2797C12.4792 10.088 12.7167 9.99219 13 9.99219C13.2833 9.99219 13.5208 10.088 13.7125 10.2797C13.9042 10.4714 14 10.7089 14 10.9922C14 11.2755 13.9042 11.513 13.7125 11.7047C13.5208 11.8964 13.2833 11.9922 13 11.9922ZM9 15.9922C8.71667 15.9922 8.47917 15.8964 8.2875 15.7047C8.09583 15.513 8 15.2755 8 14.9922C8 14.7089 8.09583 14.4714 8.2875 14.2797C8.47917 14.088 8.71667 13.9922 9 13.9922C9.28333 13.9922 9.52083 14.088 9.7125 14.2797C9.90417 14.4714 10 14.7089 10 14.9922C10 15.2755 9.90417 15.513 9.7125 15.7047C9.52083 15.8964 9.28333 15.9922 9 15.9922ZM5 15.9922C4.71667 15.9922 4.47917 15.8964 4.2875 15.7047C4.09583 15.513 4 15.2755 4 14.9922C4 14.7089 4.09583 14.4714 4.2875 14.2797C4.47917 14.088 4.71667 13.9922 5 13.9922C5.28333 13.9922 5.52083 14.088 5.7125 14.2797C5.90417 14.4714 6 14.7089 6 14.9922C6 15.2755 5.90417 15.513 5.7125 15.7047C5.52083 15.8964 5.28333 15.9922 5 15.9922ZM13 15.9922C12.7167 15.9922 12.4792 15.8964 12.2875 15.7047C12.0958 15.513 12 15.2755 12 14.9922C12 14.7089 12.0958 14.4714 12.2875 14.2797C12.4792 14.088 12.7167 13.9922 13 13.9922C13.2833 13.9922 13.5208 14.088 13.7125 14.2797C13.9042 14.4714 14 14.7089 14 14.9922C14 15.2755 13.9042 15.513 13.7125 15.7047C13.5208 15.8964 13.2833 15.9922 13 15.9922Z"
                    fill="#FFFFFF"
                  />
                </svg>
                <p className="text-white">
                  {formatDateMMDDYYYY(renderPhoto?.[dateKey])}
                </p>
              </div>
              <div className="absolute bottom-6 left-0 right-0 flex w-full items-center justify-center">
                <div
                  onClick={() => {
                    setIsFullViewOpen(true);
                  }}
                  className="flex cursor-pointer items-center justify-center gap-x-2 text-base text-white"
                >
                  <img
                    draggable={false}
                    src={panZoomIcon}
                    alt="Full View Icon"
                  />
                  Click for Full View
                </div>
              </div>

              {renderFiles}

              {photos?.length > 1 && (
                <>
                  <button
                    type="button"
                    className="hover:bg-gray-50 absolute left-3 top-1/2 -translate-y-1/2 cursor-pointer rounded-full bg-white p-2 shadow-lg"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleNavigation("prev");
                    }}
                  >
                    <svg
                      className="h-5 w-5"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M15 19l-7-7 7-7"
                      />
                    </svg>
                  </button>
                  <button
                    type="button"
                    className="hover:bg-gray-50 absolute right-3 top-1/2 -translate-y-1/2 cursor-pointer rounded-full bg-white p-2 shadow-lg"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleNavigation("next");
                    }}
                  >
                    <svg
                      className="h-5 w-5"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M9 5l7 7-7 7"
                      />
                    </svg>
                  </button>
                </>
              )}
              {photos?.length > 0 && !disabled && (
                <button
                  type="button"
                  className="absolute right-3 top-3 h-[50px] w-[50px] cursor-pointer bg-transparent  hover:text-white"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    setIsDeleteModalOpen(true);
                  }}
                >
                  <img
                    draggable={false}
                    src={deleteFIleIcon}
                    className="h-full w-full object-contain p-2"
                    alt="delete"
                  />
                </button>
              )}
            </div>
            <div className=" flex w-full flex-col gap-y-2">
              {hasVerifiedDisplay && (
                <>
                  {renderPhoto?.status === "verified" ? (
                    <div className="flex items-center gap-x-2">
                      <svg
                        width="20"
                        height="20"
                        viewBox="0 0 20 20"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          d="M8.6 11.8L6.45 9.65C6.26667 9.46667 6.03333 9.375 5.75 9.375C5.46667 9.375 5.23333 9.46667 5.05 9.65C4.86667 9.83333 4.775 10.0667 4.775 10.35C4.775 10.6333 4.86667 10.8667 5.05 11.05L7.9 13.9C8.1 14.1 8.33333 14.2 8.6 14.2C8.86667 14.2 9.1 14.1 9.3 13.9L14.95 8.25C15.1333 8.06667 15.225 7.83333 15.225 7.55C15.225 7.26667 15.1333 7.03333 14.95 6.85C14.7667 6.66667 14.5333 6.575 14.25 6.575C13.9667 6.575 13.7333 6.66667 13.55 6.85L8.6 11.8ZM10 20C8.61667 20 7.31667 19.7375 6.1 19.2125C4.88333 18.6875 3.825 17.975 2.925 17.075C2.025 16.175 1.3125 15.1167 0.7875 13.9C0.2625 12.6833 0 11.3833 0 10C0 8.61667 0.2625 7.31667 0.7875 6.1C1.3125 4.88333 2.025 3.825 2.925 2.925C3.825 2.025 4.88333 1.3125 6.1 0.7875C7.31667 0.2625 8.61667 0 10 0C11.3833 0 12.6833 0.2625 13.9 0.7875C15.1167 1.3125 16.175 2.025 17.075 2.925C17.975 3.825 18.6875 4.88333 19.2125 6.1C19.7375 7.31667 20 8.61667 20 10C20 11.3833 19.7375 12.6833 19.2125 13.9C18.6875 15.1167 17.975 16.175 17.075 17.075C16.175 17.975 15.1167 18.6875 13.9 19.2125C12.6833 19.7375 11.3833 20 10 20Z"
                          fill="#0A8525"
                        />
                      </svg>
                      <p className="text-base text-lasalle-green">
                        <span className="text-vampire-grey">
                          Verified: {renderPhoto?.verified_date}
                        </span>
                      </p>
                    </div>
                  ) : (
                    <div className="flex items-center gap-x-2">
                      <svg
                        width="20"
                        height="20"
                        viewBox="0 0 20 20"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          d="M5 11.5C5.41667 11.5 5.77083 11.3542 6.0625 11.0625C6.35417 10.7708 6.5 10.4167 6.5 10C6.5 9.58333 6.35417 9.22917 6.0625 8.9375C5.77083 8.64583 5.41667 8.5 5 8.5C4.58333 8.5 4.22917 8.64583 3.9375 8.9375C3.64583 9.22917 3.5 9.58333 3.5 10C3.5 10.4167 3.64583 10.7708 3.9375 11.0625C4.22917 11.3542 4.58333 11.5 5 11.5ZM10 11.5C10.4167 11.5 10.7708 11.3542 11.0625 11.0625C11.3542 10.7708 11.5 10.4167 11.5 10C11.5 9.58333 11.3542 9.22917 11.0625 8.9375C10.7708 8.64583 10.4167 8.5 10 8.5C9.58333 8.5 9.22917 8.64583 8.9375 8.9375C8.64583 9.22917 8.5 9.58333 8.5 10C8.5 10.4167 8.64583 10.7708 8.9375 11.0625C9.22917 11.3542 9.58333 11.5 10 11.5ZM15 11.5C15.4167 11.5 15.7708 11.3542 16.0625 11.0625C16.3542 10.7708 16.5 10.4167 16.5 10C16.5 9.58333 16.3542 9.22917 16.0625 8.9375C15.7708 8.64583 15.4167 8.5 15 8.5C14.5833 8.5 14.2292 8.64583 13.9375 8.9375C13.6458 9.22917 13.5 9.58333 13.5 10C13.5 10.4167 13.6458 10.7708 13.9375 11.0625C14.2292 11.3542 14.5833 11.5 15 11.5ZM10 20C8.61667 20 7.31667 19.7375 6.1 19.2125C4.88333 18.6875 3.825 17.975 2.925 17.075C2.025 16.175 1.3125 15.1167 0.7875 13.9C0.2625 12.6833 0 11.3833 0 10C0 8.61667 0.2625 7.31667 0.7875 6.1C1.3125 4.88333 2.025 3.825 2.925 2.925C3.825 2.025 4.88333 1.3125 6.1 0.7875C7.31667 0.2625 8.61667 0 10 0C11.3833 0 12.6833 0.2625 13.9 0.7875C15.1167 1.3125 16.175 2.025 17.075 2.925C17.975 3.825 18.6875 4.88333 19.2125 6.1C19.7375 7.31667 20 8.61667 20 10C20 11.3833 19.7375 12.6833 19.2125 13.9C18.6875 15.1167 17.975 16.175 17.075 17.075C16.175 17.975 15.1167 18.6875 13.9 19.2125C12.6833 19.7375 11.3833 20 10 20Z"
                          fill="#CC8A0F"
                        />
                      </svg>
                      <p className="text-base text-ochre">Not Verified</p>
                    </div>
                  )}
                </>
              )}
              {hasDescriptionDisplay && (
                <div className="flex items-center gap-x-2">
                  {/* <svg
                    width="18"
                    height="20"
                    viewBox="0 0 18 20"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M2 19.9922C1.45 19.9922 0.979167 19.7964 0.5875 19.4047C0.195833 19.013 0 18.5422 0 17.9922V3.99219C0 3.44219 0.195833 2.97135 0.5875 2.57969C0.979167 2.18802 1.45 1.99219 2 1.99219H3V0.992188C3 0.708854 3.09583 0.471354 3.2875 0.279687C3.47917 0.0880208 3.71667 -0.0078125 4 -0.0078125C4.28333 -0.0078125 4.52083 0.0880208 4.7125 0.279687C4.90417 0.471354 5 0.708854 5 0.992188V1.99219H13V0.992188C13 0.708854 13.0958 0.471354 13.2875 0.279687C13.4792 0.0880208 13.7167 -0.0078125 14 -0.0078125C14.2833 -0.0078125 14.5208 0.0880208 14.7125 0.279687C14.9042 0.471354 15 0.708854 15 0.992188V1.99219H16C16.55 1.99219 17.0208 2.18802 17.4125 2.57969C17.8042 2.97135 18 3.44219 18 3.99219V17.9922C18 18.5422 17.8042 19.013 17.4125 19.4047C17.0208 19.7964 16.55 19.9922 16 19.9922H2ZM2 17.9922H16V7.99219H2V17.9922ZM9 11.9922C8.71667 11.9922 8.47917 11.8964 8.2875 11.7047C8.09583 11.513 8 11.2755 8 10.9922C8 10.7089 8.09583 10.4714 8.2875 10.2797C8.47917 10.088 8.71667 9.99219 9 9.99219C9.28333 9.99219 9.52083 10.088 9.7125 10.2797C9.90417 10.4714 10 10.7089 10 10.9922C10 11.2755 9.90417 11.513 9.7125 11.7047C9.52083 11.8964 9.28333 11.9922 9 11.9922ZM5 11.9922C4.71667 11.9922 4.47917 11.8964 4.2875 11.7047C4.09583 11.513 4 11.2755 4 10.9922C4 10.7089 4.09583 10.4714 4.2875 10.2797C4.47917 10.088 4.71667 9.99219 5 9.99219C5.28333 9.99219 5.52083 10.088 5.7125 10.2797C5.90417 10.4714 6 10.7089 6 10.9922C6 11.2755 5.90417 11.513 5.7125 11.7047C5.52083 11.8964 5.28333 11.9922 5 11.9922ZM13 11.9922C12.7167 11.9922 12.4792 11.8964 12.2875 11.7047C12.0958 11.513 12 11.2755 12 10.9922C12 10.7089 12.0958 10.4714 12.2875 10.2797C12.4792 10.088 12.7167 9.99219 13 9.99219C13.2833 9.99219 13.5208 10.088 13.7125 10.2797C13.9042 10.4714 14 10.7089 14 10.9922C14 11.2755 13.9042 11.513 13.7125 11.7047C13.5208 11.8964 13.2833 11.9922 13 11.9922ZM9 15.9922C8.71667 15.9922 8.47917 15.8964 8.2875 15.7047C8.09583 15.513 8 15.2755 8 14.9922C8 14.7089 8.09583 14.4714 8.2875 14.2797C8.47917 14.088 8.71667 13.9922 9 13.9922C9.28333 13.9922 9.52083 14.088 9.7125 14.2797C9.90417 14.4714 10 14.7089 10 14.9922C10 15.2755 9.90417 15.513 9.7125 15.7047C9.52083 15.8964 9.28333 15.9922 9 15.9922ZM5 15.9922C4.71667 15.9922 4.47917 15.8964 4.2875 15.7047C4.09583 15.513 4 15.2755 4 14.9922C4 14.7089 4.09583 14.4714 4.2875 14.2797C4.47917 14.088 4.71667 13.9922 5 13.9922C5.28333 13.9922 5.52083 14.088 5.7125 14.2797C5.90417 14.4714 6 14.7089 6 14.9922C6 15.2755 5.90417 15.513 5.7125 15.7047C5.52083 15.8964 5.28333 15.9922 5 15.9922ZM13 15.9922C12.7167 15.9922 12.4792 15.8964 12.2875 15.7047C12.0958 15.513 12 15.2755 12 14.9922C12 14.7089 12.0958 14.4714 12.2875 14.2797C12.4792 14.088 12.7167 13.9922 13 13.9922C13.2833 13.9922 13.5208 14.088 13.7125 14.2797C13.9042 14.4714 14 14.7089 14 14.9922C14 15.2755 13.9042 15.513 13.7125 15.7047C13.5208 15.8964 13.2833 15.9922 13 15.9922Z"
                      fill="#525252"
                    />
                  </svg> */}
                  <p className="text-base text-vampire-grey">
                    {renderPhoto?.[descriptionKey]}
                  </p>
                </div>
              )}
            </div>

            {/* <FullViewPhotoModal
              isOpen={isFullViewOpen}
              //   isOpen={true}
              onClose={() => setIsFullViewOpen(false)}
              imageSrc={renderPhoto?.src}
              imageAlt={renderPhoto?.name}
            /> */}
            <ModalFileViewer
              isOpen={isFullViewOpen}
              setIsOpen={setIsFullViewOpen}
              title={title || renderPhoto?.name}
              fileUrl={renderPhoto?.src}
              fileType={renderPhoto?.type}
            />
          </div>
        ) : (
          <div
            className="flex h-[300px] cursor-pointer flex-col items-center justify-center space-y-3 rounded-md border border-dashed border-[#003460]"
            onClick={() => fileInputRef.current.click()}
          >
            <div className="h-[30px] w-[30px]">
              <img
                src={fileUploadIcon}
                className="h-full w-full object-contain"
                alt="upload"
              />
            </div>
            <p className="text-sm text-vampire-grey">
              {isUploading ? "Uploading..." : "There are no uploaded file yet"}
            </p>
            <p className="text-xs text-gray-500">
              {acceptedTypes === "image/*"
                ? "Images only"
                : "PDF and Images supported"}
            </p>
          </div>
        )}
      </Modal>
    </>
  );
};

export default ViewPhotosModal;
